import type {
    DOMConversionMap,
    DOMConversionOutput,
    EditorConfig,
    ElementFormatType,
    ElementNode,
    LexicalNode,
    NodeKey,
    RangeSelection,
} from 'lexical'
import { $createInlineIndicatorsNode } from '../../../../nodes/InlineIndicatorsNode/InlineIndicatorsNode'
import {
    $createNestedNoteNode,
    $createNoteContainerNode,
} from '../NoteContainerNode/NoteContainerNode'
import type {
    AdamParagraphPayload,
    SerializedAdamParagraphNode,
} from './AdamParagraphNodeHeadless'
import { AdamParagraphNodeHeadless } from './AdamParagraphNodeHeadless'

const $insertNewParagraphAfterNode = (
    node: ElementNode,
    restoreSelection: boolean,
): AdamParagraphNode => {
    const container = node.getTopLevelElement()
    const paragraph = $createAdamParagraphNode()
    paragraph.setDirection(node.getDirection())
    paragraph.setIndent(node.getIndent())
    paragraph.setFormat(node.getFormatType())
    const nestedNode = $createNestedNoteNode(() => paragraph)
    container?.insertAfter(nestedNode, restoreSelection)
    return paragraph
}

class AdamParagraphNode extends AdamParagraphNodeHeadless {
    static getType() {
        return 'adam_paragraph'
    }

    getEnabledOptions() {
        return {
            download: false,
            attachment: true,
            comment: true,
            tag: true,
            reaction: true,
            delete: true,
            duplicate: true,
            viewDetails: true,
            aiPrompts: true,
            integration: false,
            rowHeader: false,
            columnHeader: false,
            annotate: false,
            startPresentation: false,
        }
    }

    getDefaultDetailsTab() {
        return 'Related'
    }

    canBeLastItem() {
        return true
    }

    isDraggable() {
        return false
    }

    canDropInside() {
        return false
    }

    isDropTarget() {
        return false
    }

    extractWithChild(): boolean {
        return false
    }

    canIndent(): boolean {
        return false
    }

    isInline(): boolean {
        return false
    }

    static clone(node: AdamParagraphNode) {
        return $createAdamParagraphNode(node.__payload, node.__key)
    }

    static importJSON(
        serializedNode: SerializedAdamParagraphNode,
    ): AdamParagraphNode {
        const node = $createAdamParagraphNode()
        node.setFormat(serializedNode.format)
        node.setIndent(serializedNode.indent)
        node.setDirection(serializedNode.direction)
        return node
    }

    exportJSON() {
        return super.exportJSON()
    }

    static importDOM(): DOMConversionMap | null {
        return {
            p: () => ({
                conversion: convertParagraphElement,
                priority: 1,
            }),
        }
    }

    createDOM(config: EditorConfig) {
        const dom = document.createElement('p')
        if (config.theme.paragraph !== undefined) {
            dom.classList.add(config.theme.paragraph)
        }
        return dom
    }

    updateDOM() {
        return false
    }

    setId(id: string) {
        const self = this.getWritable()
        self.__payload.id = id
    }
    insertNewAfter(_: RangeSelection, restoreSelection: boolean): LexicalNode {
        return $insertNewParagraphAfterNode(this, restoreSelection)
    }

    collapseAtStartOffset(): boolean {
        if (super.collapseAtStartOffset()) return true

        return true
    }

    cleanForPaste() {
        this.__payload = {
            ...this.__payload,
            id: '',
        }
    }
}

function convertParagraphElement(element: HTMLElement): DOMConversionOutput {
    const node = $createNoteContainerNode()
    return {
        after: (nodes) => {
            const paragraphNode = $createAdamParagraphNode()

            if (element.style) {
                node.setFormat(element.style.textAlign as ElementFormatType)
                const indent = parseInt(element.style.textIndent, 10) / 20
                if (indent > 0) {
                    node.setIndent(indent)
                }
            }

            nodes.forEach((child) => {
                paragraphNode.append(child)
            })

            return [
                paragraphNode,
                $createInlineIndicatorsNode({
                    itemType: 'note',
                }),
            ]
        },
        node,
    }
}

function $createAdamParagraphNode(
    payload?: Partial<AdamParagraphPayload>,
    nodeKey?: NodeKey,
) {
    return new AdamParagraphNode(payload, nodeKey)
}

function $isAdamParagraphNode(
    node: LexicalNode | null | undefined,
): node is AdamParagraphNode {
    return node instanceof AdamParagraphNode
}

export {
    $insertNewParagraphAfterNode,
    $isAdamParagraphNode,
    $createAdamParagraphNode,
    AdamParagraphNode,
}
